export interface FloatingPlacementOptions {
  anchorLeft: number
  anchorRight: number
  containerWidth: number
  floatingWidth: number
  edgePadding?: number
  gap?: number
  preferredSide?: 'left' | 'right'
}

export interface FloatingPlacement {
  left: number
  maxWidth: number
  side: 'left' | 'right'
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function getFloatingPlacement({
  anchorLeft,
  anchorRight,
  containerWidth,
  floatingWidth,
  edgePadding = 8,
  gap = 8,
  preferredSide = 'left',
}: FloatingPlacementOptions): FloatingPlacement {
  const maxWidth = Math.max(containerWidth - edgePadding * 2, 0)
  const effectiveWidth = Math.min(Math.max(floatingWidth, 0), maxWidth)
  const minLeft = edgePadding
  const maxLeft = Math.max(
    minLeft,
    containerWidth - edgePadding - effectiveWidth
  )

  const leftCandidate = anchorLeft - gap - effectiveWidth
  const rightCandidate = anchorRight + gap

  const leftFits = leftCandidate >= minLeft
  const rightFits = rightCandidate <= maxLeft

  if (preferredSide === 'left') {
    if (leftFits) return { left: leftCandidate, maxWidth, side: 'left' }
    if (rightFits) return { left: rightCandidate, maxWidth, side: 'right' }
  } else {
    if (rightFits) return { left: rightCandidate, maxWidth, side: 'right' }
    if (leftFits) return { left: leftCandidate, maxWidth, side: 'left' }
  }

  const availableLeft = Math.max(0, anchorLeft - gap - edgePadding)
  const availableRight = Math.max(
    0,
    containerWidth - edgePadding - (anchorRight + gap)
  )
  const fallbackSide =
    preferredSide === 'left'
      ? availableLeft >= availableRight
        ? 'left'
        : 'right'
      : availableRight >= availableLeft
        ? 'right'
        : 'left'
  const fallbackCandidate =
    fallbackSide === 'left' ? leftCandidate : rightCandidate

  return {
    left: clamp(fallbackCandidate, minLeft, maxLeft),
    maxWidth,
    side: fallbackSide,
  }
}
